/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.1.0
 * @since 1.0.0
 */
import { ChangeEvent, FormEvent, PureComponent } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { initTranslation } from '../../../../modules/internationalization/constants';
import { IPageAuthTranslation } from '../../../../modules/internationalization/types';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { initialRecovery, loginRecovery, loginRecoveryAnswer } from '../../actions/recoveryActions';
import { makeSelectRecovery, makeSelectRecoveryQuestion } from '../../selectors/recoverySelectors';
import { ILoginRecoveryAnswerRequest, ILoginRecoveryState } from '../../types/authTypes';
import RecoveryForm from './RecoveryForm';

interface IRecoveryFormProps {
    initialRecovery: () => void;
    loginRecovery: (login: string) => void;
    recoveryAnswer: (form: ILoginRecoveryAnswerRequest) => void;
    translation: IPageAuthTranslation;
}

interface IRecoveryFormState {
    login: string;
    answer: string;
}

interface IConnectedProps {
    recovery: ILoginRecoveryState | null;
    recoveryQuestion: string | null;
}

/**
 * @description recovery block. Can be of 3 types.
 * @class RecoveryFormContainer
 * @extends PureComponent<IAuthFormProps, IAuthFormState>
 *
 *  and 'ussd' - for message for ussd query.
 */
export class RecoveryFormContainer extends PureComponent<IRecoveryFormProps & IConnectedProps, IRecoveryFormState> {

    public static defaultProps = {
        answer: '',
        backToAuth: '',
        buttonBack: '',
        buttonSubmit: '',
        captcha: '',
        labelLogin: '',
        subTitle: '',
        title: '',
        ussd: '',
        loginRecovery: (login: string) => login,
        initialRecovery: () => null,
        recoveryAnswer: () => null,
        translation: initTranslation,
    };
    public state = {
        answer: '',
        login: '',
    };

    public componentDidMount() {
        this.props.initialRecovery();
    }

    public render() {
        const {
            translation,
            recoveryQuestion,
        } = this.props;
        const { login, answer } = this.state;
        return (
            <RecoveryForm
                inputHandler={this.inputHandler}
                login={login}
                question={recoveryQuestion}
                submitHandler={this.submitHandler}
                type={this.getType()}
                userAnswer={answer}
                {...translation.formRecovery}
            />
        );
    }

    public inputHandler = <T extends keyof IRecoveryFormState>(event: ChangeEvent<HTMLInputElement>, field: string) => {
        const value = event.target.value;
        const newState = {
            [field]: value,
        };
        this.setState(newState as { [P in T]: IRecoveryFormState[P] });
    };

    private submitHandler = (event: FormEvent, type: string): void => {
        event.preventDefault();
        if (type === 'login' && this.state.login) {
            this.props.loginRecovery(this.state.login);
        } else if (type === 'answer' && this.state.answer) {
            this.props.recoveryAnswer({
                answer: this.state.answer,
                username: this.state.login,
            });
        }
    };

    private getType(): string {
        const { recovery } = this.props;
        if (recovery) {
            if (!recovery.question) {
                return 'ussd';
            } else {
                return 'question';
            }
        }
        return 'login';
    }
}

const mapStateToProps = createStructuredSelector<IRootReducer, IRecoveryFormProps, IConnectedProps>({
    recovery: makeSelectRecovery(),
    recoveryQuestion: makeSelectRecoveryQuestion(),
});

export const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        loginRecovery: (login: string) => dispatch(loginRecovery(login)),
        initialRecovery: () => dispatch(initialRecovery()),
        recoveryAnswer: (form: ILoginRecoveryAnswerRequest) => dispatch(loginRecoveryAnswer(form)),
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps, mapDispatchToProps)(RecoveryFormContainer as any) as any;
